import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../../config";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";


interface Contest {
  id: number;
  contest_name: string;
  contest_date: string;
  result_date: string;
  reward: string;
  reward_worth: number;
  entry_fee: string;
  total_slots: number;
  registration_count: number;
  contest_flag: number;
}

const ManageContests: React.FC = () => {
  const [contests, setContests] = useState<Contest[]>([]);
  const navigate = useNavigate();

  const fetchContests = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/contests`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.contests)) {
        setContests(data.contests);
      } else {
        console.error("Unexpected data format:", data);
      }
    } catch (err) {
      console.error("Error fetching contests:", err);
    }
  };

  useEffect(() => {
    fetchContests();
  }, []);

  const handleSetLive = async (id: number) => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/contest/${id}/set-live`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      const data = await res.json();
      if (res.ok) {
        Swal.fire({
          title: "Success",
          text: data.message,
          icon: "success",
          confirmButtonText: "OK",
          customClass: {
            confirmButton: "bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700",
          },
          buttonsStyling: false,
        });

        fetchContests();
      } else {
        Swal.fire({
          title: "Error",
          text: data.error,
          icon: "error",
          confirmButtonText: "OK",
          customClass: {
            confirmButton: "bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700",
          },
          buttonsStyling: false,
        });

      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "Server error",
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700",
        },
        buttonsStyling: false,
      });

    }
  };

  const handleDelete = async (id: number) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this contest!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      customClass: {
        confirmButton: "bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 mr-2",
        cancelButton: "bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400",
      },
      buttonsStyling: false,
    });


    if (!confirm.isConfirmed) return;

    try {
      const res = await fetch(`${API_BASE_URL}/admin/contest/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      const data = await res.json();
      if (res.ok) {
        Swal.fire({
          title: "Deleted!",
          text: data.message,
          icon: "success",
          confirmButtonText: "OK",
          customClass: {
            confirmButton: "bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700",
          },
          buttonsStyling: false,
        });

        fetchContests();
      } else {
        Swal.fire({
          title: "Error",
          text: data.error,
          icon: "error",
          confirmButtonText: "OK",
          customClass: {
            confirmButton: "bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700",
          },
          buttonsStyling: false,
        });

      }
    } catch (err) {
      Swal.fire({
        title: "Error",
        text: "Server error",
        icon: "error",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700",
        },
        buttonsStyling: false,
      });
    }
  };

  return (
    <div className="pt-24 max-w-6xl mx-auto px-4 pb-10">
      <h2 className="text-2xl font-bold mb-6 text-indigo-700">Manage Contests</h2>
      <div className="mb-4">
        <button
          onClick={() => navigate("/admin/dashboard")}
          className="bg-gray-100 text-indigo-700 px-4 py-2 rounded hover:bg-gray-200 transition"
        >
          ← Back to Home
        </button>
      </div>

      <div className="overflow-x-auto shadow rounded-lg border">
        <table className="min-w-full text-sm text-left">
          <thead className="bg-indigo-100">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Reward</th>
              <th className="px-4 py-2">Entry Fee</th>
              <th className="px-4 py-2">Slots</th>
              <th className="px-4 py-2">Live</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contests.length > 0 ? (
              contests.map((contest) => (
                <tr key={contest.id} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{contest.contest_name}</td>
                  <td className="px-4 py-2">
                    {new Date(contest.contest_date).toLocaleString()}
                  </td>
                  <td className="px-4 py-2">{contest.reward} (₹{contest.reward_worth})</td>
                  <td className="px-4 py-2">₹{contest.entry_fee}</td>
                  <td className="px-4 py-2">
                    {contest.registration_count}/{contest.total_slots}
                  </td>
                  <td className="px-4 py-2">
                    {contest.contest_flag === 1 ? (
                      <span className="text-green-600 font-semibold">LIVE</span>
                    ) : (
                      <button
                        className="text-blue-600 underline"
                        onClick={() => handleSetLive(contest.id)}
                      >
                        Set Live
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-2 space-x-2">
                    <button
                      className="text-indigo-600 underline"
                      onClick={() => navigate(`/admin/edit-contest/${contest.id}`, { state: { contest } })}
                    >
                      Edit
                    </button>
                    <button
                      className="text-red-600 underline"
                      onClick={() => handleDelete(contest.id)}
                    >
                      Delete
                    </button>
                  </td>

                </tr>
              ))
            ) : (
              <tr>
                <td className="px-4 py-3 text-center text-gray-500" colSpan={7}>
                  No contests found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageContests;
